import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { LuPlus, LuExternalLink, LuUser, LuCreditCard } from 'react-icons/lu';
import aviso, { textoDoErro } from '../lib/aviso';
import Select from './Select';
import { PERMISSOES, temPermissao } from '../config/permissions';
import {
    AssinaturaService, ROTULO_SITUACAO, SITUACAO, formatarData,
    type Assinatura, type AssinaturaPayload,
} from '../services/AssinaturaService';
import { ProductService, type Product } from '../services/ProductService';
import './AbaAssinatura.css';

/* ─────────────────────────────────────────────────────────────
   Aba Assinatura da oportunidade (25/10/2026).

   Quem monta a tira de abas já decide se esta aparece: só existe
   com a funcionalidade `assinaturas` ligada para a empresa.

   O ponto da aba é economizar três passos. Preenchendo aqui, a
   assinatura nasce **já ligada ao negócio e ao contato dele** —
   o contato não é perguntado porque a resposta já está na tela:
   é o contato desta oportunidade.
   ───────────────────────────────────────────────────────────── */

interface Props {
    conversationId: number;
    /** Só para o texto de apoio: quem é o contato que será vinculado. */
    nomeContato?: string | null;
    /** Valor do negócio, usado como sugestão quando o produto não tem preço. */
    valorNegocio?: number | null;
}

interface Rascunho {
    identificador: string;
    nome: string;
    produtoId: string;
    situacao: number;
    expiraEm: string;
    valor: string;
    quantidade: string;
    origem: string;
}

const VAZIO: Rascunho = {
    identificador: '', nome: '', produtoId: '', situacao: SITUACAO.ativa,
    expiraEm: '', valor: '', quantidade: '1', origem: '',
};

const moeda = (v?: number | null) =>
    v == null ? '—' : v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const AbaAssinatura = ({ conversationId, nomeContato, valorNegocio }: Props) => {
    const podeGerenciar = temPermissao(PERMISSOES.assinaturasGerenciar);

    const [assinatura, setAssinatura] = useState<Assinatura | null>(null);
    const [carregando, setCarregando] = useState(true);
    const [produtos, setProdutos] = useState<Product[]>([]);
    const [rascunho, setRascunho] = useState<Rascunho>(VAZIO);
    const [salvando, setSalvando] = useState(false);

    const carregar = useCallback(async () => {
        setCarregando(true);
        try {
            const r = await AssinaturaService.getByNegocio(conversationId);
            setAssinatura(r.dados ?? null);
        } catch (erro) {
            aviso.erro(textoDoErro(erro, 'Não foi possível carregar a assinatura.'));
        } finally {
            setCarregando(false);
        }
    }, [conversationId]);

    useEffect(() => { void carregar(); }, [carregar]);

    useEffect(() => {
        void ProductService.getAll()
            .then(r => setProdutos(r.dados ?? []))
            .catch(() => setProdutos([]));
    }, []);

    const opcoesProduto = useMemo(() => produtos.map(p => ({
        value: String(p.produtoId),
        label: p.nome,
        meta: p.preco ? moeda(p.preco) : undefined,
    })), [produtos]);

    /* O preço do produto sugere o valor; sem preço, cai no valor do próprio
       negócio, que é o número que a pessoa acabou de ver na ficha. */
    const escolherProduto = (valor: string) => {
        const produto = produtos.find(p => String(p.produtoId) === valor);
        const sugerido = produto?.preco || valorNegocio || 0;
        setRascunho(r => ({
            ...r,
            produtoId: valor,
            valor: r.valor || (sugerido ? String(sugerido) : r.valor),
        }));
    };

    const salvar = async () => {
        if (!rascunho.identificador.trim()) {
            aviso.erro('O identificador é obrigatório.');
            return;
        }
        setSalvando(true);
        try {
            const payload: AssinaturaPayload = {
                identificador: rascunho.identificador.trim(),
                nome: rascunho.nome.trim() || null,
                produtoId: rascunho.produtoId ? Number(rascunho.produtoId) : null,
                situacao: rascunho.situacao,
                expiraEm: rascunho.expiraEm || null,
                aderiuEm: new Date().toISOString(),
                valor: rascunho.valor ? Number(rascunho.valor) : null,
                quantidade: Number(rascunho.quantidade) || 1,
                origem: rascunho.origem.trim() || null,
                observacao: null,
                /* É isto que liga as três pontas numa chamada só. */
                conversationId,
            };
            const r = await AssinaturaService.create(payload);
            aviso.ok('Assinatura criada e vinculada a este negócio.');
            setAssinatura(r.dados);
            setRascunho(VAZIO);
        } catch (erro) {
            aviso.erro(textoDoErro(erro, 'Não foi possível criar a assinatura.'));
        } finally {
            setSalvando(false);
        }
    };

    if (carregando) {
        return <div className="aba-assinatura"><div className="lk-skeleton" /><div className="lk-skeleton" /></div>;
    }

    /* Já existe: a aba vira leitura. Editar é na tela de Assinaturas, que tem a
       ficha inteira — repetir o formulário aqui criaria dois lugares para a
       mesma edição, e eles divergem. */
    if (assinatura) {
        return (
            <div className="aba-assinatura">
                <div className="aas-ficha">
                    <div className="aas-ficha-topo">
                        <span className="aas-ident">{assinatura.identificador}</span>
                        <span className={`asg-selo asg-selo-${assinatura.situacao}`}>
                            {ROTULO_SITUACAO[assinatura.situacao]}
                        </span>
                    </div>

                    <dl className="aas-dados">
                        <div><dt>Plano</dt><dd>{assinatura.produtoNome || '—'}</dd></div>
                        <div><dt>Vence em</dt><dd>{formatarData(assinatura.expiraEm)}</dd></div>
                        <div><dt>Valor</dt><dd>{moeda(assinatura.valor)}</dd></div>
                        <div><dt>Quantidade</dt><dd>{assinatura.quantidade}</dd></div>
                        <div>
                            <dt>Contato vinculado</dt>
                            <dd>{assinatura.contatoNome || <span className="lk-empty-value">nenhum</span>}</dd>
                        </div>
                        <div><dt>Origem</dt><dd>{assinatura.origem || '—'}</dd></div>
                    </dl>

                    <Link to="/assinaturas" className="lk-btn">
                        <LuExternalLink size={14} /> Abrir em Assinaturas
                    </Link>
                </div>
            </div>
        );
    }

    if (!podeGerenciar) {
        return (
            <div className="aba-assinatura">
                <div className="lk-blank">
                    <div className="lk-blank-title">Sem assinatura neste negócio</div>
                    <p className="lk-blank-text">Você não tem permissão para criar assinaturas.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="aba-assinatura">
            <p className="aas-intro">
                <LuCreditCard size={14} />
                {nomeContato
                    ? <>Criar a assinatura aqui já liga ela a este negócio e ao contato <strong>{nomeContato}</strong>.</>
                    : <>Criar a assinatura aqui já liga ela a este negócio.</>}
            </p>

            <div className="aas-form">
                <label>
                    <span>Identificador *</span>
                    <input
                        type="text"
                        value={rascunho.identificador}
                        onChange={e => setRascunho({ ...rascunho, identificador: e.target.value })}
                        placeholder="O login do assinante no seu serviço"
                    />
                </label>
                <label>
                    <span>Nome</span>
                    <input
                        type="text"
                        value={rascunho.nome}
                        onChange={e => setRascunho({ ...rascunho, nome: e.target.value })}
                        placeholder={nomeContato ?? ''}
                    />
                </label>
                <label>
                    <span>Plano</span>
                    <Select
                        options={opcoesProduto}
                        value={rascunho.produtoId}
                        onChange={escolherProduto}
                        placeholder={produtos.length ? 'Escolha um produto' : 'Nenhum produto cadastrado'}
                        disabled={produtos.length === 0}
                        searchFrom={8}
                    />
                </label>
                <label>
                    <span>Situação</span>
                    <Select
                        options={[SITUACAO.ativa, SITUACAO.expirada, SITUACAO.cancelada]
                            .map(v => ({ value: String(v), label: ROTULO_SITUACAO[v] }))}
                        value={String(rascunho.situacao)}
                        onChange={v => setRascunho({ ...rascunho, situacao: Number(v) })}
                    />
                </label>
                <label>
                    <span>Vence em</span>
                    <input
                        type="date"
                        value={rascunho.expiraEm}
                        onChange={e => setRascunho({ ...rascunho, expiraEm: e.target.value })}
                    />
                </label>
                <label>
                    <span>Valor</span>
                    <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={rascunho.valor}
                        onChange={e => setRascunho({ ...rascunho, valor: e.target.value })}
                    />
                </label>
                <label>
                    <span>Quantidade</span>
                    <input
                        type="number"
                        min="1"
                        value={rascunho.quantidade}
                        onChange={e => setRascunho({ ...rascunho, quantidade: e.target.value })}
                    />
                </label>
                <label>
                    <span>Origem</span>
                    <input
                        type="text"
                        value={rascunho.origem}
                        onChange={e => setRascunho({ ...rascunho, origem: e.target.value })}
                        placeholder="Revenda, canal, parceiro"
                    />
                </label>
            </div>

            <div className="aas-acoes">
                <button
                    type="button"
                    className="lk-btn is-primary"
                    disabled={salvando || !rascunho.identificador.trim()}
                    onClick={() => void salvar()}
                >
                    {salvando ? 'Criando…' : <><LuPlus size={14} /> Criar assinatura</>}
                </button>
                {nomeContato && (
                    <span className="aas-nota">
                        <LuUser size={13} /> O contato {nomeContato} será vinculado junto.
                    </span>
                )}
            </div>
        </div>
    );
};

export default AbaAssinatura;
